/* eslint-disable @typescript-eslint/no-explicit-any */
import { InjectionToken, Optional, Provider, Type } from '@angular/core';
import { TableApiQuery } from './resource-table-query-adapter.service';

export interface ResourceTablePresetData {
  query: TableApiQuery;
  columns: string[];
}

export interface ResourceTablePreset {
  id: string;
  name: string;
  hash: string;
  data: ResourceTablePresetData;
  order: number;
}

export interface IResourceTablePresetAdapter {
  getPresets(key: string): Promise<ResourceTablePreset[]>;
  savePreset(
    key: string,
    preset: Partial<ResourceTablePreset>,
  ): Promise<ResourceTablePreset>;
  deletePreset(key: string, preset: ResourceTablePreset): Promise<void>;
}

export const RESOURCE_TABLE_PRESET_ADAPTER =
  new InjectionToken<IResourceTablePresetAdapter>(
    'Resource table preset adapter',
  );

export interface IResourceTablePresetConfig {
  cacheMillis?: number;
}

export const RESOURCE_TABLE_PRESET_CONFIG =
  new InjectionToken<IResourceTablePresetConfig>(
    'Resource table preset config',
  );

export class ResourceTablePresetAdapterCache {
  private readonly cache = new Map<
    string,
    {
      timestamp: number;
      data: ResourceTablePreset[];
    }
  >();

  constructor(private readonly cacheMillis: number) {}

  get(key: string): ResourceTablePreset[] | undefined {
    const entry = this.cache.get(key);
    if (entry) {
      const now = Date.now();
      if (now - entry.timestamp < this.cacheMillis) {
        return entry.data;
      } else {
        this.cache.delete(key);
      }
    }

    return undefined;
  }

  set(key: string, presets: ResourceTablePreset[]): void {
    const now = Date.now();

    this.cache.set(key, {
      timestamp: now,
      data: presets,
    });
  }

  delete(key: string, preset: ResourceTablePreset): void {
    const entry = this.cache.get(key);
    if (entry) {
      const updated = entry.data.filter((p) => p.id !== preset.id);
      this.cache.set(key, {
        timestamp: entry.timestamp,
        data: updated,
      });
    }
  }

  clear(): void {
    this.cache.clear();
  }
}

export class ResourceTablePresetService implements IResourceTablePresetAdapter {
  constructor(
    private readonly adapter: IResourceTablePresetAdapter,
    private readonly cache?: ResourceTablePresetAdapterCache,
  ) {}

  async getPresets(key: string): Promise<ResourceTablePreset[]> {
    const cached = this.cache?.get(key);

    if (cached !== undefined) {
      return cached;
    }

    const presets = await this.adapter.getPresets(key);

    this.cache?.set(key, presets);

    return presets;
  }

  async savePreset(
    key: string,
    preset: Partial<ResourceTablePreset>,
  ): Promise<ResourceTablePreset> {
    const savedPreset = await this.adapter.savePreset(key, preset);
    const presets = [
      ...(this.cache?.get(key) ?? []).filter((p) => p.id !== savedPreset.id),
      savedPreset,
    ];
    this.cache?.set(key, presets);

    return savedPreset;
  }

  async deletePreset(key: string, preset: ResourceTablePreset): Promise<void> {
    await this.adapter.deletePreset(key, preset);
    this.cache?.delete(key, preset);
  }
}

export const provideResourceTablePresetAdapter = (
  adapter: Type<IResourceTablePresetAdapter>,
  config?: { cacheMillis?: number },
): Provider[] => {
  const providers: Provider[] = [
    {
      provide: RESOURCE_TABLE_PRESET_ADAPTER,
      useExisting: adapter,
    },
  ];

  if (config?.cacheMillis) {
    providers.push({
      provide: RESOURCE_TABLE_PRESET_CONFIG,
      useValue: { cacheMillis: config.cacheMillis },
    });

    providers.push({
      provide: ResourceTablePresetAdapterCache,
      useFactory: (cfg: IResourceTablePresetConfig) =>
        new ResourceTablePresetAdapterCache(cfg.cacheMillis || 0),
      deps: [RESOURCE_TABLE_PRESET_CONFIG],
    });
  }

  providers.push({
    provide: RESOURCE_TABLE_PRESET_ADAPTER,
    useFactory: (
      adapterInstance: IResourceTablePresetAdapter,
      cache?: ResourceTablePresetAdapterCache,
    ) => {
      return new ResourceTablePresetService(adapterInstance, cache);
    },
    deps: [adapter, [new Optional(), ResourceTablePresetAdapterCache]],
  });

  return providers;
};

export const tablePresetHash = async (
  data?: Partial<ResourceTablePresetData>,
) => {
  const sortObject = (obj: any): any => {
    if (Array.isArray(obj)) {
      return obj.map(sortObject);
    }

    if (obj !== null && typeof obj === 'object') {
      return Object.keys(obj)
        .sort()
        .reduce((result, key) => {
          result[key] = sortObject(obj[key]);
          return result;
        }, {} as { [key: string]: any });
    }

    return obj;
  };

  const str2buffer = (str: string) => {
    const ab = new ArrayBuffer(str.length);
    const buffer = new Uint8Array(ab);
    for (let i = 0; i < str.length; i++) {
      buffer[i] = str.charCodeAt(i);
    }
    return buffer;
  };
  const sorted = sortObject(data ?? {});
  const json = JSON.stringify(sorted);

  const buffer = str2buffer(json);
  const hashBuffer = await crypto.subtle.digest('SHA-1', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  return hashHex;
};
